import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CrudService } from 'src/core/services/crud/crud.service';
import { FilterItemDto } from 'src/core/models/request/filter-item.dto';
import { FilterOperator } from 'src/core/models/request/filter-operator.enum';
import { ListRequestDto } from 'src/core/models/request/list-request.dto';
import { ListResponseDto } from 'src/core/models/request/list-response.dto';
import { SorterItemDto } from 'src/core/models/request/sorter-item.dto';
import { environment } from 'src/environments/environment';
import { CrmContactDto } from '../models/crm-contact.dto';
import { CrmContactSummaryDto } from '../models/crm-contact-summary.dto';
import { PhoneConflictCheckInputDto } from '../models/phone-conflict-check-input.dto';
import { PhoneConflictCheckResultDto } from '../models/phone-conflict-check-result.dto';
import { ContactImportSegmentsDto } from '../models/contact-import-segments.dto';

export interface CrmContactListRequest {
  skipCount: number;
  maxResultCount: number;
  quickSearchTerm?: string | null;
  segment?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class CrmContactService {
  private readonly crudService = inject(CrudService);
  private readonly http = inject(HttpClient);

  getList(input: CrmContactListRequest): Observable<ListResponseDto<CrmContactDto>> {
    const filters: FilterItemDto[] = [];
    const sorters: SorterItemDto[] = [
      {
        field: 'FirstName',
        direction: 'asc',
      },
    ];

    if (input.quickSearchTerm?.trim()) {
      filters.push({
        field: 'quickSearchTerm',
        operator: 2,
        value: input.quickSearchTerm.trim(),
      });
    }

    const request: ListRequestDto = {
      skipCount: input.skipCount,
      maxResultCount: input.maxResultCount,
      filters,
      sorters,
    };

    return this.crudService.get<CrmContactDto>(CrmContactDto, request);
  }

  getContactCountBySegments(segmentIds: number[]): Observable<number> {
    const filters: FilterItemDto[] = [
      { field: 'segmentIds', operator: FilterOperator.In, value: segmentIds },
      { field: 'isActive', operator: FilterOperator.Equals, value: true },
    ];

    const request: ListRequestDto = {
      skipCount: 0,
      maxResultCount: 1,
      filters,
      sorters: [],
    };

    return this.crudService
      .get<CrmContactDto>(CrmContactDto, request)
      .pipe(map(response => response.totalCount ?? 0));
  }

  create(input: Partial<CrmContactDto>): Observable<Partial<CrmContactDto>> {
    return this.crudService.insertWithoutId(CrmContactDto, input);
  }

  update(input: Partial<CrmContactDto> & { id: number }): Observable<void> {
    return this.crudService.update<void>(CrmContactDto, input);
  }

  delete(id: number) {
    return this.crudService.delete(CrmContactDto, id);
  }

  getByPhoneNumber(phoneNumber: string): Observable<CrmContactDto | null> {
    const filters: FilterItemDto[] = [
      { field: 'phoneNumber', operator: 2, value: phoneNumber },
    ];
    const request: ListRequestDto = {
      skipCount: 0,
      maxResultCount: 1,
      filters,
      sorters: [],
    };
    return this.crudService.get<CrmContactDto>(CrmContactDto, request).pipe(
      map(response => response.items?.[0] ?? null),
    );
  }

  getById(id: number): Observable<CrmContactDto> {
    return this.crudService.abpGetById<CrmContactDto>(CrmContactDto, id);
  }

  reassignPhoneNumber(input: {
    phoneNumber: string;
    sourceContactId: number;
    targetContactId: number | null;
    conversationUid?: string | null;
  }): Observable<void> {
    return this.http.post<void>(
      `${environment.apis.default.url}/${CrmContactDto.apiUrl}/reassign-phone-number`,
      input,
    );
  }

  /**
   * Read before a CSV import: the segment definitions of the tenant, matched against the segment
   * column of the already loaded file to find the names that would have to be created.
   */
  getImportSegments(): Observable<ContactImportSegmentsDto> {
    return this.http.get<ContactImportSegmentsDto>(
      `${environment.apis.default.url}/${CrmContactDto.apiUrl}/import-segments`,
    );
  }

  checkPhoneConflicts(input: PhoneConflictCheckInputDto): Observable<PhoneConflictCheckResultDto> {
    return this.http.post<PhoneConflictCheckResultDto>(
      `${environment.apis.default.url}/${CrmContactDto.apiUrl}/check-phone-conflicts`,
      input,
    );
  }

  deactivate(id: number): Observable<void> {
    return this.http.post<void>(`${environment.apis.default.url}/${CrmContactDto.apiUrl}/deactivate`, {
      id,
    });
  }

  activate(id: number): Observable<void> {
    return this.http.post<void>(`${environment.apis.default.url}/${CrmContactDto.apiUrl}/activate`, {
      id,
    });
  }

  getContactSummaries(input: CrmContactListRequest): Observable<ListResponseDto<CrmContactSummaryDto>> {
    const filters: { field: string; operator: number; value: string }[] = [];

    if (input.quickSearchTerm?.trim()) {
      filters.push({ field: 'quickSearchTerm', operator: 2, value: input.quickSearchTerm.trim() });
    }

    const sorters = [{ field: 'FirstName', direction: 'asc' }];

    let params = new HttpParams()
      .set('skipCount', input.skipCount.toString())
      .set('maxResultCount', input.maxResultCount.toString())
      .set('filters', JSON.stringify(filters))
      .set('sorters', JSON.stringify(sorters));

    return this.http.get<ListResponseDto<CrmContactSummaryDto>>(
      `${environment.apis.default.url}/${CrmContactDto.apiUrl}/summaries`,
      { params },
    );
  }

}